import React, { useEffect, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { Spinner } from 'react-bootstrap'
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Form,
  FormGroup,
  FormText,
  Input,
  Row,
} from 'reactstrap'
import { api } from '../../utils/index'
import SetTitle from '../../utils/set_title'

import { ToastContainer } from 'react-toastify'
import { notify } from '../../utils/notify'
import FileInput from '../../components/molecules/FileInput'
import ClienteSelect from '../../components/molecules/ClienteSelect'
import { useFileUpload } from '../../hooks'
import Header from '../../components/Headers/Header'

import { all, create } from 'mathjs'

const math = create(all)

const getCategoryGenClientName = (job) => {
  if (job?.cliente?.nome) {
    return job.cliente.nome
  }

  return job?.brand || ''
}

const DEFAULT_QUESTION_PROMPT = `
Sei un 'SEO specialist' con anni di esperienza. Il tuo obiettivo è scrivere un testo SEO di circa {words_count} parole per una pagina di listing prodotto dell’ecommerce di {brand}, utilizzando un linguaggio HTML valido come descritto di seguito.

Assicurati di rispettare le seguenti linee guida:

**Struttura HTML:**
- Utilizza solo i tag <h2>, <h3>, <p>, e <strong>.
- Organizza il contenuto in sezioni chiare con titoli pertinenti, senza un eccessivo numero di paragrafi.

**Tone of Voice:**
- Mantieni un tono descrittivo, professionale e informativo.
- Usa un linguaggio persuasivo ma non aggressivo per invogliare l’acquisto.
- Sii coinvolgente e persuasivo, spingendo l'utente a interagire con il contenuto e a esplorare i prodotti.
- Sii preciso e conciso, fornendo informazioni di valore senza dilungarti inutilmente.
- Non utilizzare colloquialismi, slang o formule troppo ricercate.
- {tone_of_voice}

**Ottimizzazione SEO:**
- Integra le parole chiave in modo naturale e fluido. 
- Inserisci le keyword in modo naturale e scorrevole, evitando 'keyword stuffing' e ripetizioni forzate.
- Nomina [Brand] in modo organico senza ripetizioni eccessive.
- Utilizza <strong> per evidenziare parole chiave principali e secondarie.
- Varia la keyword principale utilizzando sinonimi e termini correlati.

**Contenuto:**
- Descrivi dettagliatamente i prodotti della categoria, evidenziando caratteristiche chiave dei prodotti presenti
- Non citare marchi specifici se non necessario.
- Spiega perché i prodotti [Brand] sono preferenziali rispetto ad altre opzioni di mercato.
- Sottolinea la vasta gamma di prodotti di qualità e prezzi competitivi disponibili online e nei negozi.
- Evita le seguenti parole e i relativi sinonimi: {stopwords}

**Benefici per l'utente:**
- Evidenzia come gli utenti possono beneficiare dell’acquisto su {brand}
- Rivolgiti direttamente al lettore e invita all’esplorazione dei prodotti.

**Chiusura implicita:**
- Termina naturalmente, senza un blocco "Conclusioni", ma con un invito implicito all'acquisto o esplorazione, come "[chiusura con invito all’acquisto esempio: Esplora la gamma di prodotti su [Brand] e trova la soluzione perfetta per le tue esigenze]".

**Restrizioni:**
- Non inserire link.
- Il testo deve essere originale.
- Non fornire specifiche di singoli prodotti e non riferirsi a fonti esterne.

# Steps
1. Identifica le parole chiave principali e secondarie dalle informazioni che ti fornisco sui prodotti presenti nella categoria.
2. Organizza il contenuto in sezioni con sottotitoli appropriati usando <h2> e più raramente anche <h3>.
3. Redigi paragrafi informativi rispettando il tono e linguaggio richiesto.
4. Integra le parole chiave attraverso il testo in modo naturale, usando anche <strong> per enfatizzarle.
5. Assicurati di sottolineare i vantaggi dell’acquisto su {brand} lungo tutto il testo, in modo non eccessivo.
6. Concludi il testo con un invito implicito all’esplorazione e all'acquisto dei prodotti.

# Output Format
Fornisci il testo già formattato con paragrafi e strong, rispettando le specifiche indicate.

# Notes
- Evita conclusioni esplicite, lasciando un invito naturale alla fine.

Specifiche sulla pagina per la scrittura:

Listing prodotto per cui scrivere il testo: 
{category_name}

Url listing prodotto per cui scrivere il testo: 
{category_url}

Informazioni aggiuntive (se disponibili) per arricchire il testo su questa pagina specifica (non citarle letteralmente):
{extra_context}

Al fine di scrivere un testo ricco di informazioni ti fornisco l'elenco dei prodotti presenti nei listing e le descrizioni e le caratteristiche. Usa le informazioni fornite più comuni e ricorrenti come riferimento per prendere spunti, arricchire il testo di informazioni utili per chi deve acquistare e definire il contesto ma non nominarli (al massimo le marche) all'interno del testo. MOLTO IMPORTANTE: alcune caratteristiche potrebbero essere specifiche solo ad alcuni prodotti, quindi evita di generalizzare. Ecco l'elenco:
{data}
`

const CategoryTextGenForm = () => {
  SetTitle('Generatore Testi per Categorie')
  const { id } = useParams()
  const history = useHistory()

  const [name, setName] = useState('')
  const [brand, setBrand] = useState('')
  const [clienti, setClienti] = useState([])
  const [selectedClientId, setSelectedClientId] = useState('')
  const [toneOfVoice, setToneOfVoice] = useState('')
  const [stopwords, setStopwords] = useState('')
  const [customPrompt, setCustomPrompt] = useState(DEFAULT_QUESTION_PROMPT)
  const [wordsCount, setWordsCount] = useState(500) // New state for words count
  const [language, setLanguage] = useState('') // Language for generated text (empty = Italian default)
  const [file, setFile] = useState(null)
  const [jobStatus, setJobStatus] = useState(null)
  const [progress, setProgress] = useState('0/0')
  const [isEditMode, setIsEditMode] = useState(false) // New state to track edit/view mode
  const { upload, loading } = useFileUpload()

  const handleSubmit = async (event) => {
    event.preventDefault()
    if (isEditMode) {
      // Prevent form submission if in edit mode
      notify(
        'Non è possibile creare un nuovo job in modalità visualizzazione. Utilizza il pulsante "Riprova falliti" se disponibile.',
        'warning'
      )
      return
    }
    if (!name || !selectedClientId || !file) {
      // File is only required in create mode
      notify('Per favore, compila il nome del job, seleziona un cliente e carica un file.', 'error')
      return
    }

    let formData = new FormData()
    formData.append('name', name)
    formData.append('brand', brand)
    if (selectedClientId) {
      formData.append('cliente', selectedClientId)
    }
    formData.append('tone_of_voice', toneOfVoice)
    formData.append('stopwords', stopwords)
    formData.append('custom_prompt', customPrompt)
    formData.append('words_count', wordsCount) // Add wordsCount to form data
    formData.append('language', language) // Add language to form data
    formData.append('file', file)

    try {
      await upload('post', `category-text-gen/`, formData, {
        onSuccess: (data) => {
          notify("Job creato con successo. L'elaborazione inizierà a breve.", 'success')
          setTimeout(() => history.push('/category-text-gen'), 500)
        },
        onError: (errorMessage, err) => {
          notify(err.response?.data?.error || 'Si è verificato un errore.', 'warning')
        },
      })
    } catch (err) {
      console.error('Category Text Gen submission failed:', err)
      notify("Si è verificato un errore inaspettato durante l'invio.", 'error')
    }
  }

  const handleUpdate = async () => {
    if (!id) return
    if (!selectedClientId) {
      notify('Seleziona un cliente.', 'error')
      return
    }
    try {
      await api.put(`category-text-gen/${id}/`, {
        custom_prompt: customPrompt,
        brand,
        cliente: selectedClientId || null,
      })
      notify('Prompt aggiornato con successo.', 'success')
      setTimeout(() => window.location.reload(), 500)
    } catch (err) {
      console.error('Category Text Gen update failed:', err)
      const errorMessage =
        err.response?.data?.error || "Si è verificato un errore durante l'aggiornamento."
      notify(errorMessage, 'error')
    }
  }

  const handleRetry = async () => {
    if (!id) return
    try {
      await api.post(`category-text-gen/${id}/retry_failed/`)
      notify('Il processo di recupero per i job falliti è stato avviato.', 'success')
      // Optionally, redirect or update the status on the page
      setTimeout(() => history.push('/category-text-gen'), 500)
    } catch (err) {
      notify(
        err.response?.data?.error || 'Si è verificato un errore durante il tentativo di recupero.',
        'error'
      )
    }
  }

  const fetchClienti = async () => {
    try {
      const response = await api.get('clienti/')
      setClienti(Array.isArray(response.data) ? response.data : response.data?.results || [])
    } catch (err) {
      console.error('Failed to fetch clienti', err)
    }
  }

  useEffect(() => {
    setIsEditMode(!!id) // Set edit mode if ID is present
    fetchClienti()

    if (id) {
      api
        .get(`category-text-gen/${id}/`)
        .then((res) => {
          const job = res.data
          setName(job.name)
          setBrand(getCategoryGenClientName(job))
          setSelectedClientId(job?.cliente?.id ? String(job.cliente.id) : '')
          setToneOfVoice(job.tone_of_voice || '')
          setStopwords(job.stopwords || '')
          setCustomPrompt(job.custom_prompt || '')
          setWordsCount(job.words_count || 500) // Set wordsCount from job details, default to 500
          setLanguage(job.language || '') // Set language from job details
          setJobStatus(job.status)
          setProgress(job.progress || '0/0')
        })
        .catch((err) => {
          console.error('Failed to fetch job details', err)
          notify('Impossibile caricare i dettagli del job.', 'error')
          history.push('/category-text-gen') // Redirect if job not found or error
        })
    }
  }, [id, history]) // Depend on id and history for potential redirect

  return (
    <Header
      iconClass="fa-solid fa-file-lines text-white"
      title="Generatore Testi per Categorie"
      description={
        'Genera descrizioni ottimizzate per la SEO per le pagine di categoria, analizzando i prodotti presenti sulla pagina.'
      }
    >
      <div className="pb-5">
        <Card className="bg-secondary shadow">
          <CardHeader className="bg-white border-0">
            <Row className="align-items-center">
              <Col md={10}>
                <h1 className="mb-0">{id ? 'Visualizza' : 'Nuovo'} Job di Generazione Testi</h1>
              </Col>
              <Col md={2} style={{ textAlign: 'right' }}>
                <Button color="primary" onClick={() => history.push('/category-text-gen')}>
                  &larr; Torna alla Lista
                </Button>
              </Col>
            </Row>
          </CardHeader>
          <CardBody>
            {loading ? (
              <div className="text-center">
                <Spinner animation="border" role="status">
                  <span className="sr-only">Loading...</span>
                </Spinner>
              </div>
            ) : (
              <Form onSubmit={handleSubmit} id="category-text-gen-form">
                <h6 className="heading-small text-muted mb-4">Configurazione del Job</h6>
                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <label htmlFor="name" className="form-control-label">
                        Nome del Job
                      </label>
                      <Input
                        className="form-control-alternative"
                        type="text"
                        placeholder="es. Collezione Estiva [brand]"
                        id="name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        required
                        disabled={isEditMode} // Disable in edit mode
                      />
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    {/* FileInput should be disabled or hidden in edit mode */}
                    {!isEditMode && (
                      <FileInput
                        onFileChange={setFile}
                        accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                        helpText={
                          <>
                            Colonne richieste: <strong>name</strong>, <strong>link</strong>.
                            Opzionale: <strong>extra_context</strong> (contesto aggiuntivo per
                            pagina incluso nel prompt).
                          </>
                        }
                        required={!id}
                      />
                    )}
                    {isEditMode && (
                      <FormGroup>
                        <label className="form-control-label">File caricato</label>
                        <p className="form-control-static">
                          {jobStatus === 'CREATED' || jobStatus === 'RETRIEVING_DATA'
                            ? 'File in elaborazione'
                            : 'File caricato in precedenza'}
                        </p>
                        <FormText color="muted">
                          Per modificare le categorie, è necessario creare un nuovo job.
                        </FormText>
                      </FormGroup>
                    )}
                  </Col>
                </Row>
                <hr className="my-4" />
                <h6 className="heading-small text-muted mb-4">Parametri di Generazione Testo</h6>
                <Row>
                  <Col md={6}>
                    <ClienteSelect
                      id="cliente"
                      label="Cliente canonico"
                      clienti={clienti}
                      selectedClientId={selectedClientId}
                      onChange={(value) => {
                        setSelectedClientId(value)
                        const selectedCliente = clienti.find(
                          (cliente) => String(cliente.id) === String(value)
                        )
                        if (selectedCliente?.nome) {
                          setBrand(selectedCliente.nome)
                        }
                      }}
                      isRequired
                      helpText="Il backend sincronizza automaticamente il brand legacy."
                      showCreateLink
                    />
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <label htmlFor="brand" className="form-control-label">
                        Nome del Brand legacy
                      </label>
                      <Input
                        className="form-control-alternative"
                        type="text"
                        placeholder="Inserisci il nome del brand"
                        id="brand"
                        value={brand}
                        onChange={(e) => setBrand(e.target.value)}
                        required={!selectedClientId}
                      />
                      <FormText color="muted">
                        Campo legacy di compatibilità. Se selezioni un cliente, viene riallineato
                        automaticamente.
                      </FormText>
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <label htmlFor="tone_of_voice" className="form-control-label">
                        Tone of Voice (Opzionale)
                      </label>
                      <Input
                        className="form-control-alternative"
                        type="text"
                        placeholder="es. Giocoso ed energico"
                        id="tone_of_voice"
                        value={toneOfVoice}
                        onChange={(e) => setToneOfVoice(e.target.value)}
                        disabled={isEditMode} // Disable in edit mode
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <label htmlFor="words_count" className="form-control-label">
                        Numero di Parole (Opzionale)
                      </label>
                      <Input
                        className="form-control-alternative"
                        type="number"
                        placeholder="es. 500"
                        id="words_count"
                        value={wordsCount}
                        onChange={(e) => setWordsCount(e.target.value)}
                        disabled={isEditMode} // Disable in edit mode
                      />
                      <FormText color="muted">
                        Numero approssimativo di parole per il testo generato. Default: 500.
                      </FormText>
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <label htmlFor="language" className="form-control-label">
                        Lingua (Opzionale)
                      </label>
                      <Input
                        className="form-control-alternative"
                        type="text"
                        placeholder="es. English, French, German"
                        id="language"
                        value={language}
                        onChange={(e) => setLanguage(e.target.value)}
                        disabled={isEditMode}
                      />
                      <FormText color="muted">
                        Lingua del testo generato. Se vuoto, default italiano.
                      </FormText>
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <label htmlFor="stopwords" className="form-control-label">
                        Stopwords (Opzionale)
                      </label>
                      <Input
                        className="form-control-alternative"
                        type="text"
                        placeholder="es. economico, gratis, limitato"
                        id="stopwords"
                        value={stopwords}
                        onChange={(e) => setStopwords(e.target.value)}
                        disabled={isEditMode} // Disable in edit mode
                      />
                      <FormText color="muted">
                        Lista di parole separate da virgola da evitare nel testo generato.
                      </FormText>
                    </FormGroup>
                  </Col>
                </Row>

                <Row>
                  <Col>
                    <FormGroup>
                      <label htmlFor="custom_prompt" className="form-control-label">
                        Custom Prompt (Opzionale)
                      </label>
                      <Input
                        className="form-control-alternative"
                        type="textarea"
                        rows="5"
                        placeholder="Se fornito, questo prompt sostituirà quello di default per la generazione del testo."
                        id="custom_prompt"
                        value={customPrompt}
                        onChange={(e) => setCustomPrompt(e.target.value)}
                        disabled={false}
                      />
                      <FormText color="muted">
                        Devi inserire queste variabili nel prompt: {'{brand}'}, {'{category_name}'},{' '}
                        {'{data}'}, {'{stopwords}'}, {'{tone_of_voice}'}, {'{words_count}'}.
                      </FormText>
                    </FormGroup>
                  </Col>
                </Row>
                <Row className="mt-4 mb-3">
                  <Col className="text-center">
                    {id && jobStatus === 'ERROR' && (
                      <Button
                        color="warning"
                        size="lg"
                        onClick={handleRetry}
                        className="mr-3"
                        disabled={loading}
                      >
                        Riprova falliti
                      </Button>
                    )}
                    {isEditMode && (
                      <Button
                        color="primary"
                        size="lg"
                        onClick={handleUpdate}
                        disabled={loading}
                        className="mr-3"
                      >
                        Aggiorna Prompt
                      </Button>
                    )}
                    {isEditMode && math.evaluate(progress || '0/1') === 1 && (
                      <a
                        href={`${api.defaults.baseURL}category-text-gen/${id}/download_excel/`}
                        download
                      >
                        <Button
                          color="success"
                          size="lg"
                          type="button"
                          disabled={loading}
                          className="mr-3"
                        >
                          <i className="fas fa-file-download" /> Scarica Excel
                        </Button>
                      </a>
                    )}
                    {!isEditMode && (
                      <Button color="info" size="lg" type="submit" disabled={loading}>
                        {loading ? <Spinner size="sm" /> : 'Crea Job'}
                      </Button>
                    )}
                  </Col>
                </Row>
              </Form>
            )}
            <ToastContainer />
          </CardBody>
        </Card>
      </div>
    </Header>
  )
}
export default CategoryTextGenForm
